import { Injectable } from '@angular/core';
import {
  CanActivate,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  Router
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { MenuService, PageAccessCheckResponse } from '../services/menu.service';
import { ApiResponse } from '../model/Response';

/** Geçerli timestamp ile yanıt timestamp'i arasındaki maksimum fark (ms). */
const MAX_TIMESTAMP_DIFF_MS = 30_000;

@Injectable()
export class PageAccessGuard implements CanActivate {

  constructor(
    private router: Router,
    private authService: AuthService,
    private menuService: MenuService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | boolean {

    const currentUser = this.authService.currentUserValue;
    if (!currentUser) {
      this.router.navigate(['/pages/login']);
      return false;
    }

    // Route data'da menuId tanımlanmışsa onu kullan, yoksa 0 gönder
    const menuId: number = route.data && route.data['menuId'] ? route.data['menuId'] : 0;
    // URL'den path hesapla (/main/offers gibi)
    const path = this.buildPath(state.url);

    return this.menuService.checkPageAccess(menuId, path).pipe(
      map((response: ApiResponse) => {
        const accessData: PageAccessCheckResponse = response?.responseData?.item;
        return this.validateAccess(accessData, path);
      }),
      catchError((_err) => {
        // Ağ hatası durumunda backend @PreAuthorize güvencesi devreye girer;
        // UI tarafında bloklama yapmıyoruz.
        return of(true);
      })
    );
  }

  /**
   * Backend yanıtını doğrular:
   *  1. Yanıt var mı?
   *  2. Signature alanı dolu mu? (manipülasyon tespiti)
   *  3. Timestamp max 30 saniye önce üretilmiş mi? (replay koruması)
   *  4. hasAccess true mu?
   */
  private validateAccess(accessData: PageAccessCheckResponse, path: string): boolean {
    if (!accessData) {
      this.router.navigate(['/pages/login']);
      return false;
    }

    if (!accessData.signature || accessData.signature.length === 0) {
      console.warn(`[PageAccessGuard] İmza eksik – olası response manipülasyonu: ${path}`);
      this.router.navigate(['/pages/login']);
      return false;
    }

    const now = Date.now();
    if (Math.abs(now - accessData.timestamp) > MAX_TIMESTAMP_DIFF_MS) {
      console.warn(`[PageAccessGuard] Timestamp geçersiz – olası replay saldırısı: ${path}`);
      this.router.navigate(['/pages/login']);
      return false;
    }

    if (!accessData.hasAccess) {
      this.router.navigate(['/pages/login']);
      return false;
    }

    return true;
  }

  /**
   * RouterStateSnapshot URL'sinden temiz bir path üretir.
   * Örn: "/main/offers?foo=bar" → "/main/offers"
   */
  private buildPath(url: string): string {
    return url.split('?')[0].split('#')[0];
  }
}
